/**
 * Configuration for analytics providers.
 * Determines which providers receive specific events.
 *
 * PostHog is listed here, but utils/posthogClient.js still gates every send on
 * NEXT_PUBLIC_POSTHOG_KEY being present. Until that key is set this config is
 * inert and the app behaves exactly as it did before.
 *
 * Mixpanel and OpenPanel stay switched off by default, as they were: PostHog
 * is being added alongside them, not layered on top of live duplicate
 * tracking. The three events below are the exception — they already had a
 * live Mixpanel/OpenPanel override before PostHog existed, and that routing
 * is preserved here rather than silently dropped.
 */
const analyticsConfig = {
  // Default providers if an event-specific override is not found.
  // An array of provider names: e.g., ["mixpanel"], ["openpanel"], ["posthog"]
  defaultProviders: ["posthog"],

  // Specific overrides for event names.
  // Keys are event names (case-sensitive).
  // Values are arrays of provider names.
  events: {
    clicked_checkout: ["mixpanel", "openpanel", "posthog"],
    footer_cta: ["mixpanel", "openpanel", "posthog"],
    MinOrderAlert: ["mixpanel", "openpanel", "posthog"],
  },

  // Providers for user identification calls.
  userIdentificationProviders: ["posthog"],

  // Providers for page view tracking.
  // Note: Some providers might handle page views automatically.
  // This config controls explicit trackPageView calls.
  pageViewProviders: ["posthog"],
};

/**
 * Storefront event catalogue.
 *
 * Every trackEvent name used by the buyer-facing web app lives here so the
 * names cannot drift between call sites, and so the funnels in PostHog have a
 * fixed contract to build on. Mobile and admin mirror these names — see
 * POSTHOG.md for the cross-platform table.
 *
 * Every name here has at least one call site. A name with no call site is worse
 * than no name: it reads as coverage in this file and in POSTHOG.md, builds a
 * funnel step that is silently always zero, and nothing ever fails to reveal
 * it. Ten such names were removed on 2026-09-17 — add one back only together
 * with the call site that fires it.
 */
const EVENTS = {
  // Browse
  STORE_VIEWED: "store_viewed",
  PRODUCT_VIEWED: "product_viewed",
  SEARCH_COMPLETED: "search_completed",

  // Cart
  CART_VIEWED: "cart_viewed",
  PRODUCT_ADDED_TO_CART: "product_added_to_cart",
  MIN_ORDER_ALERT: "MinOrderAlert",

  // Checkout funnel
  CHECKOUT_STARTED: "clicked_checkout",
  CHECKOUT_PROCEED_TO_PAY: "clicked_proceed_to_pay",
  CHECKOUT_PAYMENT_STARTED: "checkout_payment_started",
  ORDER_PLACED: "order_placed",
  ORDER_FAILED: "order_failed",

  // Post-purchase
  ORDER_CONFIRMATION_VIEWED: "order_confirmation_viewed",

  // Engagement
  FOOTER_CTA: "footer_cta",

  // Diagnostics
  CLIENT_LOG: "client_log",
  // Carries `trace_id`: the failed request is one click away in Tempo. Server
  // faults and dropped connections only — a 4xx is the API answering
  // correctly, and the ones worth knowing about already have their own events.
  API_REQUEST_FAILED: "api_request_failed",
};

module.exports = analyticsConfig;
module.exports.EVENTS = EVENTS;
